import React, { useRef } from 'react';
import { Formik } from 'formik';
import { Button, Modal } from 'antd';
import * as Yup from 'yup';
import { Form } from 'formik-antd';
import { isNil } from 'lodash';

import FormItem from 'components/formik/FormItem';
import { BaseModal, BaseModalFooter } from 'components/wrappers/BaseModal';
import SubmitButton from 'components/formik/SubmitButton';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { userSelector } from 'redux/selectors/authenticationSelectors';
import BaseAlert from 'components/wrappers/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { changePasswordIsActiveTwoFA } from 'redux/reducers/accountSlice';
import ButtonWithTimer from 'components/twoFactorAuth/ButtonWithTimer';
import { TwoFactorAuthSendMessageTypeEnum } from 'api/backofficeApiClient';
import { UpdatePasswordFormValues } from 'features/account/AccountSettingsPage/SecurityPage/ChangePasswordSection';
import { InvalidTwoFACodeError } from 'constants/ErrorMessages';
import { clearFormError } from 'utils/formUtils';
import { obscureEmail } from 'utils/authUtils';
import { updateStatusTwoFA } from 'redux/reducers/authenticationSlice';
import MaskedInput from 'components/formik/MaskedInput';
import { codeMask } from 'utils/validationUtils';

import styles from './EnableConfirmTwoFAModal.module.less';

type Props = {
  onClose: () => void;
  onSuccess?: () => void;
  onReset?: (status: boolean) => void;
  type: TwoFactorAuthSendMessageTypeEnum;
  passwordFormValues?: UpdatePasswordFormValues;
};

const requiredMessage = 'Field is required';

const validationSchema = Yup.object({
  code: Yup.string()
    .trim()
    .min(6, 'Code should be 6 characters minimum')
    .required(requiredMessage)
    .twoFACodeIsInvalidCheck(),
});

interface EnableTwoFAModel {
  code: string;
}

export default function EnableConfirmTwoFAModal(props: Props) {
  const { onClose, type, passwordFormValues, onSuccess, onReset } = props;
  const user = useAppSelector(userSelector);
  const dispatch = useAppDispatch();
  const formRef: any = useRef();

  const initialValues: EnableTwoFAModel = {
    code: '',
  };

  const getCorrectSuccessMessage = () => {
    switch (type) {
      case TwoFactorAuthSendMessageTypeEnum.EnableTwoFactorAuth:
        return 'Two-Factor Email Authentication was successfully enabled.';
      case TwoFactorAuthSendMessageTypeEnum.ResetPassword:
        return 'Password was successfully reset.';
    }
  };

  const handleSubmitError = (result: any) => {
    if (!isNil(result.payload?.errorDetails?.isInvalidCode)) {
      formRef?.current.setFieldValue('_formError', InvalidTwoFACodeError);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleSubmit = async (data: EnableTwoFAModel) => {
    let correctAction: any;
    let requestData: any;

    switch (type) {
      case TwoFactorAuthSendMessageTypeEnum.EnableTwoFactorAuth:
        correctAction = updateStatusTwoFA;
        requestData = { code: data.code, is2FAStatus: true };
        break;
      case TwoFactorAuthSendMessageTypeEnum.ResetPassword:
        correctAction = changePasswordIsActiveTwoFA;
        requestData = {
          code: data.code,
          oldPassword: passwordFormValues?.oldPassword,
          newPassword: passwordFormValues?.newPassword,
        };
    }

    const result = await dispatch(correctAction(requestData));

    if (correctAction.fulfilled.match(result)) {
      BaseAlert.success(getCorrectSuccessMessage());
      onSuccess && onSuccess();
      onClose();
    } else {
      handleSubmitError(result);
    }
  };

  const handleCloseModal = () => {
    if (formRef.current?.isSubmitting) return;

    if (formRef.current?.dirty) {
      return Modal.confirm({
        title: 'Are you sure? The data will not be saved.',
        icon: <span className="icon icon-attention-filled" />,
        async onOk() {
          onReset && onReset(false);
          onClose();
        },
        onCancel() {},
      });
    }
    onReset && onReset(false);
    onClose();
  };

  const getCorrectTitle = () => {
    switch (type) {
      case TwoFactorAuthSendMessageTypeEnum.EnableTwoFactorAuth:
        return 'Enable Two-Factor Email Authentication';
      case TwoFactorAuthSendMessageTypeEnum.ResetPassword:
        return 'Change Your Password';
    }
  };

  const getButtonTitle = () => {
    switch (type) {
      case TwoFactorAuthSendMessageTypeEnum.EnableTwoFactorAuth:
        return 'Enable';
      case TwoFactorAuthSendMessageTypeEnum.ResetPassword:
        return 'Change';
    }
  };

  return (
    <BaseModal
      title={getCorrectTitle()}
      onCancel={handleCloseModal}
      maxWidth={454}
      open
    >
      <Formik
        enableReinitialize
        innerRef={formRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {(formik) => (
          <Form layout="vertical">
            <div className="modal-scroll-wrapper">
              <div className={styles.enableModalText}>
                A security code has been sent to your{' '}
                {obscureEmail(user?.email || '')} email associated with this
                account. Please enter this code below to enable Two-Factor
                Authentication.
              </div>
              <FormItem name="code" label="Enter code *">
                <MaskedInput
                  name="code"
                  mask={codeMask}
                  placeholder="Enter code"
                  onChange={() => clearFormError(formik, '_formError')}
                />
              </FormItem>
              <ButtonWithTimer type={type} />
            </div>
            <BaseModalFooter>
              <Button onClick={handleCloseModal}>Cancel</Button>
              <SubmitButton type="primary">{getButtonTitle()}</SubmitButton>
            </BaseModalFooter>
          </Form>
        )}
      </Formik>
    </BaseModal>
  );
}
